import * as Yup from 'yup';
import { useState } from 'react';
import { Form, Formik } from 'formik';

import { AppRoutes } from 'config';
import { useHeader } from 'context';
import { useAppSelector } from 'store';
import { mapQueries } from 'utils/query';
import { BackOfficeRoles, ButtonStyleTypes } from 'types';
import { useListQuery as useCompaniesQuery } from 'store/api/company';
import { isDesktop, handleMutation, useSearchPagination } from 'hooks';
import { isSaleRole, hasUserRole, selectAuthUserType } from 'store/slices/auth';
import {
  useOfferMutation,
  useListQuery as useEventsQuery,
} from 'store/api/event';
import {
  List,
  Flex,
  Dialog,
  Button,
  ListHeader,
  DialogType,
  SelectField,
  ButtonTypes,
  NewEntityButton,
} from 'components';

import { Card } from './card';
import { SelectedEvent } from './types';
import { AssignTeamDialog } from './assign-team-dialog';
import {
  titlesByUserType,
  getTemplateByUserType,
  EVENT_LIST_DESKTOP_GAP,
} from './config';

export const ListPage = () => {
  const desktop = isDesktop();
  const pageData = useSearchPagination();
  const { currentPage: page, debounceQuery: search } = pageData;

  const authUserType = useAppSelector(selectAuthUserType);
  const isDisabledCreateEvent = useAppSelector(isSaleRole);
  const isAdminRep = useAppSelector(hasUserRole(BackOfficeRoles.ADMIN_REP));
  const [selectedEvent, setSelectedEvent] = useState<SelectedEvent>();

  useHeader({ pageName: 'Events' });

  const { data, ...eventsQuery } = useEventsQuery({ page, search });
  const { data: companies, ...companiesQuery } = useCompaniesQuery();

  const [offer, offerMutation] = useOfferMutation();
  handleMutation({
    ...offerMutation,
    successMessage: 'Event offered',
  });

  const companiesOptions =
    companies?.map(({ id, name }) => ({
      value: id,
      label: name,
    })) ?? [];

  const titles = authUserType && titlesByUserType[authUserType];
  const template =
    authUserType &&
    getTemplateByUserType(authUserType, isAdminRep ? 64 : undefined);

  return (
    <>
      <List
        {...mapQueries([eventsQuery, companiesQuery])}
        {...pageData}
        count={data?.count}
        mainWrapperStyles={{ paddingBottom: '16px' }}
        header={
          <ListHeader
            titles={titles}
            template={template}
            gap={`${EVENT_LIST_DESKTOP_GAP}px`}
          />
        }
        searchBarRightSlot={
          !isDisabledCreateEvent ? (
            <NewEntityButton text="New event" path={AppRoutes.Events.New} />
          ) : undefined
        }
      >
        {data?.rows?.map((item) => (
          <Card
            data={item}
            key={item.id}
            headers={titles}
            template={template}
            onEventOffer={setSelectedEvent}
            onAssignTeam={setSelectedEvent}
            gap={desktop ? EVENT_LIST_DESKTOP_GAP : undefined}
          />
        ))}
      </List>

      <AssignTeamDialog
        event={selectedEvent?.event}
        open={selectedEvent?.type === 'team'}
        onClose={() => setSelectedEvent(undefined)}
      />

      <Dialog
        title="Select company"
        open={selectedEvent?.type === 'company'}
        onClose={() => setSelectedEvent(undefined)}
      >
        {(close, _, ButtonsComponent) => (
          <Formik
            initialValues={{ companyId: undefined }}
            validationSchema={Yup.object().shape({
              companyId: Yup.string().required('Select company'),
            })}
            onSubmit={(values) => {
              offer({ ...values, id: Number(selectedEvent?.event.id) });
              close();
            }}
          >
            <Form style={{ width: '100%' }}>
              <Flex gap={16} flexDirection="column">
                <SelectField
                  onlyValue
                  name="companyId"
                  options={companiesOptions}
                />

                <ButtonsComponent type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    styleType={ButtonStyleTypes.Outline}
                  />

                  <Button text="Confirm" type={ButtonTypes.Submit} />
                </ButtonsComponent>
              </Flex>
            </Form>
          </Formik>
        )}
      </Dialog>
    </>
  );
};
